import { IconAlertTriangle, IconArrowRight } from "@tabler/icons-react"
import type { BookingDetails } from "@/app/booking-calendar/bookingCalendarApi"
import { bookingWhen } from "@/app/booking-calendar/components/AffectedBookingsList"
import { slotTypeLabelForSlug } from "@/app/booking-calendar/lib/slotTypes"
import { cn } from "@/lib/utils"
import { formatInstant } from "../../../components/automaticReassignment"
import { activeBookingOf, customerLabel, type PairingState } from "../lib/pairingState"

/** Why a listed booking cannot be placed: the server sent it without what a move needs. */
export const NOT_PLACEABLE_REASON = "Can't be placed here: the server did not send its booking request, site or slot type."

/**
 * The override's affected bookings still listed (AB#811), each a button: click to make it the active
 * one (highlighted), click again to put it down. A paired one is dulled and shows where it is going;
 * clicking it undoes the pairing. A refusal from the last Apply, and a move the batch could not make,
 * are said under their booking.
 */
export function ResolveBookingList({
  bookings,
  state,
  errors,
  failures,
  timeZone,
  disabled = false,
  showSite = false,
  onClick,
}: {
  bookings: readonly BookingDetails[]
  state: PairingState
  /** The server's reason per refused pairing, by booking request id in lower case. */
  errors: Readonly<Record<string, string>>
  /** The batch's failure detail per booking it could not move, by booking request id in lower case. */
  failures: Readonly<Record<string, string>>
  timeZone: string
  /** While a batch runs: nothing can be picked. */
  disabled?: boolean
  /** Name each booking's site, when they span more than one. */
  showSite?: boolean
  onClick: (requestId: string) => void
}) {
  if (bookings.length === 0) {
    return <p className="text-sm text-muted-foreground" data-testid="resolve-empty">No booked appointments are left for this override.</p>
  }

  return (
    <ul className="flex flex-col gap-2" aria-label="Affected appointments">
      {bookings.map(booking => {
        const placeable = activeBookingOf(booking)
        const requestId = booking.requestId ?? null
        const pairing = requestId ? state.pairings[requestId] : undefined
        const active = !!requestId && state.activeId === requestId
        const when = bookingWhen(booking, timeZone)
        const host = booking.hosts[0]?.name ?? "No host"
        const slotType = slotTypeLabelForSlug(booking.eventTypeSlug)
        // The server's ids are Guids: matched without regard to case.
        const key = requestId?.toLowerCase()
        const error = key ? errors[key] : undefined
        const failure = key ? failures[key] : undefined
        const name = customerLabel(booking)
        // A move onto another slot type (ams-8ia) says so on the dulled row.
        const newType = pairing?.typeChange ? slotTypeLabelForSlug(pairing.eventTypeSlug) : null

        return (
          <li key={booking.uid}>
            <button
              type="button"
              className={cn(
                "flex w-full flex-col gap-1 rounded-lg border p-3 text-left text-sm transition-colors",
                "hover:bg-muted/60 disabled:cursor-not-allowed",
                active && "border-primary bg-primary/10 ring-2 ring-primary",
                pairing && "opacity-60",
                error && "border-destructive",
              )}
              aria-pressed={active}
              aria-label={
                pairing
                  ? `${name}: moving to ${formatInstant(pairing.start, timeZone)} with ${pairing.hostLabel}${newType ? ` as ${newType}` : ""}. Select to undo.`
                  : active
                    ? `${name}: being placed. Select to put it down.`
                    : `${name}: select to place it.`
              }
              data-request-id={requestId ?? undefined}
              data-state={pairing ? "paired" : active ? "active" : "idle"}
              disabled={disabled || !placeable}
              title={placeable ? undefined : NOT_PLACEABLE_REASON}
              onClick={() => requestId && onClick(requestId)}
            >
              <span className="font-medium">{name}</span>
              <span className={cn(pairing && "line-through")}>
                {when.date} · {when.time}
              </span>
              <span className="text-muted-foreground">
                {host}
                {slotType && ` · ${slotType}`}
                {showSite && booking.siteName && ` · ${booking.siteName}`}
              </span>
              {pairing && (
                <span className="flex items-center gap-1 font-medium" data-testid="paired-target">
                  <IconArrowRight className="size-4 shrink-0" />
                  {formatInstant(pairing.start, timeZone)} with {pairing.hostLabel}
                  {newType && <span data-testid="paired-type"> · as {newType}</span>}
                </span>
              )}
              {!placeable && <span className="text-xs text-muted-foreground">{NOT_PLACEABLE_REASON}</span>}
            </button>
            {error && (
              <p className="mt-1 flex items-start gap-1 text-xs text-destructive" role="alert" data-pairing-error={requestId ?? undefined}>
                <IconAlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                {error}
              </p>
            )}
            {/* The last batch's failure, until the booking is paired again: then the new pairing is what counts. */}
            {failure !== undefined && !pairing && (
              <p className="mt-1 flex items-start gap-1 text-xs text-amber-700" data-batch-failure={requestId ?? undefined}>
                <IconAlertTriangle className="mt-0.5 size-3.5 shrink-0" />
                Not moved: sent to the review queue.{failure ? ` ${failure}` : ""}
              </p>
            )}
          </li>
        )
      })}
    </ul>
  )
}
